import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, ArrowDown, Send, Loader2,
    BedDouble, Bath, Users, MapPin,
    Film, Waves, Flame, Trees, CarFront, BellRing,
    ChefHat, Martini, ShoppingBasket, Car, Flag, Sailboat, Sparkles,
    Phone, MessageCircle, Mail, Check,
    X, ChevronLeft, ChevronRight, Images, Sun, Utensils,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { VILLAS } from "@/lib/content";
import { useLanguage } from "@/lib/LanguageContext";
import { apiClient, formatApiError } from "@/lib/api";
import {
    gaRentalInquirySubmit, gaVillaView, gaWhatsappClick, gaPhoneClick, gaEmailClick,
    gaCheckAvailabilityClick, newEventId, metaMatchFields,
} from "@/lib/analytics";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";

const FEATURE_ICONS = { Film, Waves, Flame, Trees, CarFront, BellRing };

// Included with every stay vs. by direct PKM reservation.
const INCLUDED = [
    { key: "housekeeping", icon: Sparkles, tier: "included" },
    { key: "golfCart", icon: CarFront, tier: "included" },
    { key: "butler", icon: BellRing, tier: "direct" },
    { key: "chef", icon: ChefHat, tier: "direct" },
];

// Optional concierge experiences — arranged upon request, not included in the nightly rate.
const PERSONALIZED = [
    { key: "dinnerChef", icon: ChefHat },
    { key: "bartender", icon: Martini },
    { key: "grocery", icon: ShoppingBasket },
    { key: "transport", icon: Car },
    { key: "golf", icon: Flag },
    { key: "yacht", icon: Sailboat },
    { key: "spa", icon: Sparkles },
    { key: "dining", icon: Utensils },
];

// Module-level guard so villa_view fires exactly once per slug even under
// React.StrictMode's double-invoke-with-cleanup pattern in development.
const VILLA_VIEW_FIRED = new Set();

const CONTACT_PHONE = "+1 (310) 770-6585";
const CONTACT_TEL = "+13107706585";
const CONTACT_WHATSAPP = "+1 (310) 770-6585";
const CONTACT_EMAIL = "info@pacifickeymanagement.com";

// =============================================================================
// AVAILABILITY INQUIRY FORM — villa-aware, sends to /api/inquiries
// =============================================================================
function AvailabilityForm({ villa, villaName }) {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        dates: "", guests: "", special: "", message: "",
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const eventId = newEventId();
        try {
            // Encode villa + dates/guests/special into the inquiry message so backend stays unchanged.
            const composed = [
                `Villa: ${villaName} (${villa.slug})`,
                form.dates && `Preferred Dates: ${form.dates}`,
                form.guests && `Guests: ${form.guests}`,
                form.special && `Special Requests: ${form.special}`,
                form.message && `Message: ${form.message}`,
            ].filter(Boolean).join("\n");
            await apiClient.post("/inquiries", {
                name: form.name,
                email: form.email,
                phone: form.phone,
                property_location: villa.community,
                service_interest: `Pacific Rentals – ${villaName}`,
                property: villaName,
                source_page: villaName,
                inquiry_type: "rental",
                preferred_dates: form.dates,
                guest_count: form.guests,
                message: composed || `Availability inquiry for ${villaName}.`,
                event_id: eventId,
                event_source_url: typeof window !== "undefined" ? window.location.href : "",
                ...metaMatchFields(),
            });
            // GA4 conversion — no PII, only categorical villa metadata.
            gaRentalInquirySubmit({
                villa_name: villaName,
                villa_slug: villa.slug,
                community: villa.community,
            }, eventId);
            setSuccess(true);
            toast.success(t("form.successToast"));
            setForm({ name: "", email: "", phone: "", dates: "", guests: "", special: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t("form.failToast"));
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="p-10 border border-pearl/20 text-pearl text-center bg-navy/40" data-testid="villa-form-success">
                <p className="overline mb-4 text-gold">{t("form.successEyebrow")}</p>
                <h3 className="font-serif text-pearl text-3xl mb-3">{t("form.successTitle")}</h3>
                <p className="text-sm text-pearl/80 max-w-md mx-auto leading-relaxed">{t("form.successBody")}</p>
            </div>
        );
    }

    const inputCls = "w-full bg-transparent border-b border-pearl/25 px-0 py-3 text-pearl placeholder:text-pearl/40 focus:outline-none focus:border-gold transition-colors duration-300 text-sm tracking-wide";
    const labelCls = "overline text-pearl/55";

    return (
        <form onSubmit={submit} data-testid="villa-availability-form" className="space-y-7">
            <div className="grid sm:grid-cols-2 gap-7">
                <label className="block">
                    <span className={labelCls}>{t("form.name")}</span>
                    <input required value={form.name} onChange={update("name")} data-testid="vform-name" className={inputCls} placeholder={t("form.namePh")} />
                </label>
                <label className="block">
                    <span className={labelCls}>{t("form.email")}</span>
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="vform-email" className={inputCls} placeholder={t("form.emailPh")} />
                </label>
                <label className="block">
                    <span className={labelCls}>{t("form.phone")}</span>
                    <input required value={form.phone} onChange={update("phone")} data-testid="vform-phone" className={inputCls} placeholder={t("form.phonePh")} />
                </label>
                <label className="block">
                    <span className={labelCls}>{t("villa.f_dates")}</span>
                    <input value={form.dates} onChange={update("dates")} data-testid="vform-dates" className={inputCls} placeholder={t("villa.f_datesPh")} />
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>{t("villa.f_guests")}</span>
                    <input value={form.guests} onChange={update("guests")} data-testid="vform-guests" className={inputCls} placeholder={t("villa.f_guestsPh")} />
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>{t("villa.f_special")}</span>
                    <input value={form.special} onChange={update("special")} data-testid="vform-special" className={inputCls} placeholder={t("villa.f_specialPh")} />
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>{t("villa.f_message")}</span>
                    <textarea rows={3} value={form.message} onChange={update("message")} data-testid="vform-message" className={`${inputCls} resize-none`} placeholder={t("villa.f_messagePh")} />
                </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button type="submit" disabled={loading} data-testid="vform-submit" className="btn-luxe-light justify-center">
                    {loading
                        ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> {t("common.sending")}</>)
                        : (<>{t("villa.requestToBook")} <Send className="w-3.5 h-3.5" strokeWidth={1.5} /></>)
                    }
                </button>
                <a href={`https://wa.me/${CONTACT_WHATSAPP.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" onClick={() => gaWhatsappClick({ cta_location: "villa_form_button" })} className="btn-luxe-gold justify-center" data-testid="vform-whatsapp">
                    {t("villa.whatsappConcierge")}
                </a>
            </div>
        </form>
    );
}

// =============================================================================
// MAIN VILLA TEMPLATE
// =============================================================================
export default function VillaDetail() {
    const { slug } = useParams();
    const { t, lang } = useLanguage();
    const villa = VILLAS.find((v) => v.slug === slug);

    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    // Full-screen premium lightbox state
    const [lb, setLb] = useState({ open: false, photos: [], index: 0 });
    const openLb = (photos, index) => setLb({ open: true, photos, index });
    const closeLb = () => setLb((s) => ({ ...s, open: false }));
    const lbGo = (dir) => setLb((s) => (s.photos.length ? { ...s, index: (s.index + dir + s.photos.length) % s.photos.length } : s));
    useEffect(() => {
        if (!lb.open) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLb();
            else if (e.key === "ArrowLeft") lbGo(-1);
            else if (e.key === "ArrowRight") lbGo(1);
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [lb.open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fire villa_view exactly once per slug (data-driven for future villas).
    // Uses a module-level Set so the guard survives React.StrictMode double-
    // mount + cleanup — a plain useRef would be reset when StrictMode aborts
    // the first mount's timer, causing the event to be permanently missed.
    useEffect(() => {
        if (!villa) return;
        if (VILLA_VIEW_FIRED.has(villa.slug)) return;
        const timer = setTimeout(() => {
            VILLA_VIEW_FIRED.add(villa.slug);
            gaVillaView({
                villa_name: lang === "es" ? villa.nameEs : villa.nameEn,
                villa_slug: villa.slug,
                community: villa.community,
            });
        }, 200);
        return () => clearTimeout(timer);
    }, [villa, lang]);

    // Build page-specific SEO metadata & VacationRental structured data.
    // This block is fully data-driven from the villa object so future villas inherit
    // the same SEO treatment automatically.
    const villaName = villa ? (lang === "es" ? villa.nameEs : villa.nameEn) : "Villa";
    const villaDesc = villa ? (lang === "es" ? villa.descriptionEs : villa.descriptionEn) : "";
    const seoTitle = villa
        ? (lang === "es"
            ? (villa.seoTitleEs || `${villaName} ${villa.community} | Renta de Villa de Lujo Los Cabos`)
            : (villa.seoTitleEn || `${villaName} ${villa.community} | Luxury Villa Rental Los Cabos`))
        : "Villa";
    const seoDescription = villa
        ? (lang === "es"
            ? (villa.seoDescEs || `Descubra ${villaName} en ${villa.community}, una villa de lujo en Los Cabos con ${villa.beds} recámaras, ${villa.baths} baños y capacidad para ${villa.sleeps} huéspedes.`)
            : (villa.seoDescEn || `Discover ${villaName} in ${villa.community}, a ${villa.beds}-bedroom luxury Los Cabos villa offering exceptional indoor-outdoor living, private amenities and personalized concierge experiences.`))
        : "Luxury villa in Los Cabos.";

    useSEO({
        title: seoTitle,
        description: seoDescription,
        path: `/pacific-rentals/${slug}`,
        image: villa?.heroImage || villa?.image,
        keywords: villa
            ? `${villaName}, ${villa.alternateName ? villa.alternateName + ", " : ""}${villa.community} villa rental, Los Cabos luxury villa, luxury villa rental Villas del Mar, 7 bedroom villa Los Cabos, Pacific Rentals, Pacific Key Management`
            : undefined,
        breadcrumbs: villa ? [
            { name: "Home", path: "/" },
            { name: "Pacific Rentals", path: "/pacific-rentals" },
            { name: villaName, path: `/pacific-rentals/${slug}` },
        ] : undefined,
        schema: villa ? {
            "@context": "https://schema.org",
            "@type": "VacationRental",
            name: villaName,
            ...(villa.alternateName ? { alternateName: villa.alternateName } : {}),
            description: villaDesc,
            url: `https://pacifickeymanagement.com/pacific-rentals/${slug}`,
            image: villa.heroImage || villa.image,
            numberOfBedrooms: villa.beds,
            numberOfBathroomsTotal: villa.baths,
            occupancy: { "@type": "QuantitativeValue", maxValue: villa.sleeps },
            address: {
                "@type": "PostalAddress",
                addressLocality: villa.community,
                addressRegion: "Baja California Sur",
                addressCountry: "MX",
            },
            containedInPlace: {
                "@type": "Place",
                name: `${villa.community}, Los Cabos`,
            },
            brand: { "@type": "Brand", name: "Pacific Rentals" },
            provider: { "@type": "Organization", name: "Pacific Key Management", url: "https://pacifickeymanagement.com" },
        } : undefined,
    });

    if (!villa) return <Navigate to="/pacific-rentals" replace />;

    const name = lang === "es" ? villa.nameEs : villa.nameEn;

    const scrollToInquiry = (e) => {
        e?.preventDefault();
        const el = document.querySelector("[data-testid='villa-inquiry-section']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div data-testid={`villa-detail-${slug}`}>
            {/* SECTION 1 — CINEMATIC HERO */}
            <section className="relative h-screen min-h-[700px] w-full overflow-hidden" data-testid="villa-hero">
                <div className="absolute inset-0">
                    <img src={villa.heroImage || villa.image} alt={name} className="w-full h-full object-cover animate-ken-burns" />
                    <div className="absolute inset-0 bg-navy/55" />
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/30 via-transparent to-navy/85" />
                </div>
                <div className="relative h-full container-luxe flex flex-col justify-end pb-20 pt-32">
                    <Link to="/pacific-rentals" className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-pearl/85 hover:text-gold transition-colors mb-10 w-fit" data-testid="villa-back-link">
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("rentals.villaBack")}
                    </Link>
                    <p className="overline text-gold animate-fade-up" style={{ animationDelay: "0.25s" }}>{villa.community}</p>
                    <h1 className="mt-5 font-serif text-pearl text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight max-w-5xl animate-fade-up" style={{ animationDelay: "0.45s" }}>
                        {name}
                    </h1>
                    <p className="mt-5 text-pearl/85 text-base sm:text-lg tracking-wide flex items-center gap-2 animate-fade-up" style={{ animationDelay: "0.6s" }}>
                        <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} /> {villa.location}
                    </p>
                    <p className="mt-7 text-pearl text-lg sm:text-xl font-serif italic animate-fade-up" style={{ animationDelay: "0.75s" }}>
                        {villa.beds} {t("villa.countBedrooms")} <span className="text-gold mx-2">·</span> Sleeps {villa.sleeps}
                    </p>
                    <p className="mt-2 text-pearl/80 text-sm tracking-wide animate-fade-up" style={{ animationDelay: "0.9s" }}>
                        {(villa.heroAmenities || []).join(" · ")}
                    </p>
                    <p className="mt-7 text-gold font-serif text-2xl sm:text-3xl animate-fade-up" style={{ animationDelay: "1.05s" }}>
                        {villa.priceLabel}
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "1.2s" }}>
                        <a href="#villa-inquiry" onClick={(e) => { scrollToInquiry(e); gaCheckAvailabilityClick({ villa_slug: villa.slug, cta_location: "villa_hero" }); }} className="btn-luxe-light" data-testid="villa-hero-cta-availability">
                            {t("villa.checkAvailability")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        <a href={`https://wa.me/${CONTACT_WHATSAPP.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" onClick={() => gaWhatsappClick({ cta_location: "villa_hero" })} className="btn-luxe-gold" data-testid="villa-hero-cta-whatsapp">
                            {t("villa.whatsappConcierge")} <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
                <div className="absolute bottom-8 inset-x-0 flex justify-center pointer-events-none animate-fade-in" style={{ animationDelay: "1.6s" }}>
                    <ArrowDown className="w-4 h-4 text-pearl/70 animate-bounce" strokeWidth={1.25} />
                </div>
            </section>

            {/* SECTION 2 — EXTRAORDINARY RETREAT */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="villa-retreat">
                <div className="container-luxe max-w-4xl text-center">
                    <Reveal>
                        <p className="overline text-gold">{t("villa.retreatEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.retreatTitle")}</h2>
                        <span className="block w-12 h-px bg-gold mx-auto my-9" />
                        <p className="text-navy/75 leading-relaxed text-lg mb-6">{t("villa.retreatP1")}</p>
                        <p className="text-navy/75 leading-relaxed text-lg">{t("villa.retreatP2")}</p>
                        {villa.aka && (
                            <p className="mt-8 text-sm text-navy/55 italic leading-relaxed" data-testid="villa-also-known-as">{t("villa.alsoKnownAs")}</p>
                        )}
                    </Reveal>
                </div>
            </section>

            {/* SECTION 3 — WHY GUESTS LOVE */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="villa-features">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("villa.whyEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.whyTitle")}</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {(villa.features || []).map((f, i) => {
                            const Icon = FEATURE_ICONS[f.icon] || Sparkles;
                            return (
                                <Reveal key={f.key} delay={i * 80} className="group" data-testid={`villa-feature-${f.key}`}>
                                    <div className="relative aspect-[5/4] overflow-hidden">
                                        <img src={f.image} alt={`${t(`villa.f_${f.key}.title`)} at ${name}, Los Cabos`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" loading="lazy" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
                                        <Icon className="absolute top-5 left-5 w-7 h-7 text-gold" strokeWidth={1.25} />
                                    </div>
                                    <h3 className="mt-7 font-serif text-2xl">{t(`villa.f_${f.key}.title`)}</h3>
                                    <span className="block w-8 h-px bg-gold my-4" />
                                    <p className="text-sm text-navy/75 leading-relaxed">{t(`villa.f_${f.key}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — LIFESTYLE GALLERY */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="villa-gallery">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-14">
                        <p className="overline text-gold">{t("villa.galleryEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.galleryTitle")}</h2>
                        <p className="mt-6 text-navy/65 leading-relaxed">{t("villa.galleryBody")}</p>
                    </Reveal>
                </div>
                {/* Immersive editorial mosaic (curated ~8-10 strongest images) */}
                <div className="grid grid-cols-12 gap-2 sm:gap-3 px-2 sm:px-3">
                    {(villa.galleryFull || []).map((g, i) => {
                        // Editorial mosaic spans — landscape-friendly rhythm tuned to the
                        // authentic Hacienda 511 photography (preserves the ocean sweep
                        // rather than cropping wide shots into portrait tiles).
                        const spans = [
                            "col-span-12 lg:col-span-7 aspect-[16/10]",
                            "col-span-12 lg:col-span-5 aspect-[16/10]",
                            "col-span-6 lg:col-span-4 aspect-[4/3]",
                            "col-span-6 lg:col-span-4 aspect-[4/3]",
                            "col-span-12 lg:col-span-4 aspect-[4/3]",
                            "col-span-12 lg:col-span-5 aspect-[16/10]",
                            "col-span-12 lg:col-span-7 aspect-[16/10]",
                            "col-span-12 aspect-[21/9]",
                        ];
                        const cls = spans[i % spans.length];
                        const caption = lang === "es" ? g.captionEs : g.captionEn;
                        return (
                            <Reveal key={i} delay={i * 40} className={`relative overflow-hidden group ${cls}`} data-testid={`villa-gallery-${i}`}>
                                <button type="button" onClick={() => openLb(villa.galleryFull, i)} className="absolute inset-0 w-full h-full cursor-pointer" aria-label={caption || "View photo"} data-testid={`villa-gallery-open-${i}`}>
                                    <img src={g.src} alt={caption} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105" loading={i === 0 ? "eager" : "lazy"} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <span className="absolute bottom-5 left-5 overline text-pearl opacity-0 group-hover:opacity-100 transition-opacity duration-500">{caption}</span>
                                </button>
                            </Reveal>
                        );
                    })}
                </div>
                {/* View All Photos — appears only when a full authentic collection exists */}
                {villa.galleryCollection && villa.galleryCollection.length > 0 && (
                    <div className="container-luxe mt-10 text-center">
                        <button type="button" onClick={() => openLb(villa.galleryCollection, 0)} className="btn-luxe-outline inline-flex items-center gap-2" data-testid="villa-view-all-photos">
                            <Images className="w-4 h-4" strokeWidth={1.5} /> {t("villa.viewAllPhotos")} ({villa.galleryCollection.length})
                        </button>
                    </div>
                )}
            </section>

            {/* Full-screen premium lightbox */}
            {lb.open && lb.photos.length > 0 && (
                <div className="fixed inset-0 z-[100] bg-navy/97 flex flex-col" data-testid="villa-lightbox" role="dialog" aria-modal="true">
                    <div className="flex items-center justify-between px-5 sm:px-8 py-5 text-pearl/90">
                        <span className="overline text-pearl/70" data-testid="lightbox-counter">{lb.index + 1} / {lb.photos.length}</span>
                        <button type="button" onClick={closeLb} aria-label="Close" data-testid="lightbox-close" className="p-2 hover:text-gold transition-colors"><X className="w-6 h-6" strokeWidth={1.5} /></button>
                    </div>
                    <div className="flex-1 flex items-center justify-center px-4 sm:px-16 pb-4 min-h-0">
                        <button type="button" onClick={() => lbGo(-1)} aria-label="Previous" data-testid="lightbox-prev" className="hidden sm:flex absolute left-4 p-3 text-pearl/80 hover:text-gold transition-colors"><ChevronLeft className="w-9 h-9" strokeWidth={1.25} /></button>
                        <figure className="max-h-full max-w-5xl flex flex-col items-center">
                            <img
                                key={lb.index}
                                src={lb.photos[lb.index].src}
                                alt={(lang === "es" ? (lb.photos[lb.index].altEs || lb.photos[lb.index].captionEs) : (lb.photos[lb.index].altEn || lb.photos[lb.index].captionEn)) || ""}
                                className="max-h-[78vh] w-auto object-contain animate-fade-in"
                                data-testid="lightbox-image"
                            />
                            {(lb.photos[lb.index].captionEn || lb.photos[lb.index].captionEs) && (
                                <figcaption className="mt-5 overline text-pearl/70 text-center">{lang === "es" ? lb.photos[lb.index].captionEs : lb.photos[lb.index].captionEn}</figcaption>
                            )}
                        </figure>
                        <button type="button" onClick={() => lbGo(1)} aria-label="Next" data-testid="lightbox-next" className="hidden sm:flex absolute right-4 p-3 text-pearl/80 hover:text-gold transition-colors"><ChevronRight className="w-9 h-9" strokeWidth={1.25} /></button>
                    </div>
                    {/* Mobile prev/next */}
                    <div className="sm:hidden flex items-center justify-center gap-16 pb-6">
                        <button type="button" onClick={() => lbGo(-1)} aria-label="Previous" className="p-3 text-pearl/80 hover:text-gold"><ChevronLeft className="w-8 h-8" strokeWidth={1.25} /></button>
                        <button type="button" onClick={() => lbGo(1)} aria-label="Next" className="p-3 text-pearl/80 hover:text-gold"><ChevronRight className="w-8 h-8" strokeWidth={1.25} /></button>
                    </div>
                </div>
            )}

            {/* SECTION 5 — INCLUDED WITH YOUR STAY */}
            <section className="bg-navy text-pearl py-24 sm:py-32" data-testid="villa-included">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("villa.includedEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("villa.includedTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-pearl/75 leading-relaxed">{t("villa.includedBody")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pearl/10">
                        {INCLUDED.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <Reveal key={s.key} delay={i * 60} className="bg-navy p-8 min-h-[230px] flex flex-col" data-testid={`villa-included-${s.key}`}>
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="font-serif text-pearl text-xl leading-snug mt-6">{t(`villa.inc_${s.key}.title`)}</h3>
                                    <p className="mt-3 text-sm text-pearl/70 leading-relaxed flex-1">{t(`villa.inc_${s.key}.body`)}</p>
                                    <span className={`mt-5 inline-flex w-fit items-center gap-2 overline ${s.tier === "included" ? "text-gold" : "text-pearl/55"}`}>
                                        <Check className="w-3.5 h-3.5" strokeWidth={2} /> {s.tier === "included" ? t("villa.badgeIncluded") : t("villa.badgeDirect")}
                                    </span>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={300} className="mt-8">
                        <p className="text-sm text-pearl/55 italic leading-relaxed max-w-3xl" data-testid="villa-included-note">{t("villa.includedNote")}</p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 6 — PERSONALIZED EXPERIENCES (optional, arranged upon request) */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="villa-personalized">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("villa.personalizedEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.personalizedTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-navy/65 leading-relaxed">{t("villa.personalizedBody")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {PERSONALIZED.map((x, i) => {
                            const Icon = x.icon;
                            return (
                                <Reveal key={x.key} delay={i * 50} className="bg-pearl p-8 min-h-[160px] flex flex-col" data-testid={`villa-experience-${x.key}`}>
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="font-serif text-lg leading-snug mt-6">{t(`villa.px_${x.key}`)}</h3>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={300} className="mt-8">
                        <p className="text-sm text-navy/55 italic leading-relaxed max-w-3xl" data-testid="villa-personalized-note">{t("villa.personalizedNote")}</p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 7 — ACCOMMODATIONS */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="villa-accommodations">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("villa.accomEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.accomTitle")}</h2>
                        <p className="mt-6 text-navy/65 leading-relaxed">{t("villa.accomBody")}</p>
                    </Reveal>
                    {/* Quick counts */}
                    <div className="grid sm:grid-cols-3 gap-px bg-navy/10 mb-14 border border-navy/10">
                        <div className="bg-pearl p-8 flex items-center gap-4">
                            <BedDouble className="w-6 h-6 text-gold" strokeWidth={1.25} />
                            <div>
                                <p className="font-serif text-3xl">{villa.beds}</p>
                                <p className="overline text-navy/55">{t("villa.countBedrooms")}</p>
                            </div>
                        </div>
                        <div className="bg-pearl p-8 flex items-center gap-4">
                            <Bath className="w-6 h-6 text-gold" strokeWidth={1.25} />
                            <div>
                                <p className="font-serif text-3xl">{villa.bathsFull}<span className="text-xl text-navy/55"> + {villa.powderRooms}</span></p>
                                <p className="overline text-navy/55">{t("villa.countBaths")}</p>
                            </div>
                        </div>
                        <div className="bg-pearl p-8 flex items-center gap-4">
                            <Users className="w-6 h-6 text-gold" strokeWidth={1.25} />
                            <div>
                                <p className="font-serif text-3xl">{villa.sleeps}</p>
                                <p className="overline text-navy/55">{t("villa.countSleeps")}</p>
                            </div>
                        </div>
                    </div>
                    {/* Bedroom configuration */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-10">
                        {(villa.bedrooms || []).map((b, i) => (
                            <Reveal key={b} delay={i * 50} data-testid={`villa-bedroom-${b}`}>
                                <p className="font-serif text-gold text-xl">0{i + 1}</p>
                                <h3 className="mt-3 font-serif text-xl leading-tight">{t(`villa.${b}.label`)}</h3>
                                <span className="block w-7 h-px bg-gold my-3" />
                                <p className="text-sm text-navy/70 leading-relaxed">{t(`villa.${b}.body`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 8 — SEASONAL RATES */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="villa-rates">
                <div className="container-luxe max-w-5xl">
                    <Reveal className="mb-14">
                        <p className="overline text-gold">{t("villa.ratesEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("villa.ratesTitle")}</h2>
                    </Reveal>
                    <div className="border-t border-navy/15">
                        {(villa.seasonalRates || []).map((r, i) => (
                            <Reveal
                                key={i}
                                delay={i * 40}
                                className="grid grid-cols-12 gap-4 sm:gap-6 items-center py-6 border-b border-navy/15"
                                data-testid={`villa-rate-${i}`}
                            >
                                <div className="col-span-7 sm:col-span-8">
                                    <p className="font-serif text-xl sm:text-2xl leading-tight">{lang === "es" ? r.periodEs : r.periodEn}</p>
                                </div>
                                <div className="col-span-5 sm:col-span-4 text-right">
                                    <span className="font-serif text-2xl text-navy">{r.rate}</span>
                                    <p className="overline text-navy/55 mt-1">{t("villa.rateSuffix")}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={300} className="mt-8 space-y-2">
                        <p className="text-sm text-navy/70 leading-relaxed">{t("villa.minStayNote")}</p>
                        <p className="text-sm text-navy/55 italic leading-relaxed">{t("villa.ratesNote")}</p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 9 — AVAILABILITY INQUIRY */}
            <section
                id="villa-inquiry"
                className="bg-navy text-pearl py-24 sm:py-32"
                data-testid="villa-inquiry-section"
            >
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20">
                    <Reveal className="lg:col-span-5 space-y-6">
                        <p className="overline text-gold">{t("villa.inquireEyebrow")}</p>
                        <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("villa.inquireTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-pearl/75 leading-relaxed">{t("villa.inquireBody")}</p>

                        {/* Contact channels */}
                        <div className="pt-6 space-y-4">
                            <a href={`tel:${CONTACT_TEL}`} onClick={() => gaPhoneClick({ cta_location: "villa_page" })} className="flex items-center gap-4 text-pearl/85 hover:text-gold transition-colors group" data-testid="villa-contact-phone">
                                <Phone className="w-5 h-5 text-gold" strokeWidth={1.25} />
                                <span>
                                    <span className="overline block text-pearl/45 mb-1">{t("villa.contactPhone")}</span>
                                    <span className="font-serif text-lg">{CONTACT_PHONE}</span>
                                </span>
                            </a>
                            <a href={`https://wa.me/${CONTACT_WHATSAPP.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" onClick={() => gaWhatsappClick({ cta_location: "villa_page" })} className="flex items-center gap-4 text-pearl/85 hover:text-gold transition-colors" data-testid="villa-contact-whatsapp">
                                <MessageCircle className="w-5 h-5 text-gold" strokeWidth={1.25} />
                                <span>
                                    <span className="overline block text-pearl/45 mb-1">{t("villa.contactWhatsapp")}</span>
                                    <span className="font-serif text-lg">{CONTACT_WHATSAPP}</span>
                                </span>
                            </a>
                            <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => gaEmailClick({ cta_location: "villa_page" })} className="flex items-center gap-4 text-pearl/85 hover:text-gold transition-colors" data-testid="villa-contact-email">
                                <Mail className="w-5 h-5 text-gold" strokeWidth={1.25} />
                                <span>
                                    <span className="overline block text-pearl/45 mb-1">{t("villa.contactEmail")}</span>
                                    <span className="font-serif text-lg">{CONTACT_EMAIL}</span>
                                </span>
                            </a>
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <AvailabilityForm villa={villa} villaName={name} />
                    </div>
                </div>
            </section>
        </div>
    );
}
